import { For, Show, createMemo, type JSX } from "solid-js";

interface StatusBarProps {
  decodeMs: () => number | null;
  diffMs: () => number | null;
  buildRowsMs: () => number | null;
  highlightMs: () => number | null;
  wrapLayoutMs: () => number | null;
  searchMs: () => number | null;
  grammarLoadMs: () => number | null;
  minimapMs: () => number | null;
  detailed: () => boolean;
  highlightDisabled: () => boolean;
}

interface Item {
  label: string;
  value: () => number | null;
  /** Returns true if this item should render with the pending '…' placeholder when value is null. */
  pendingWhenNull?: () => boolean;
  /** True if this item is only shown in detailed mode. */
  detailOnly?: boolean;
  /** When true and value is null, the item is hidden entirely (not '—'). */
  hideWhenNull?: boolean;
}

function formatMs(ms: number | null, pendingWhenNull: boolean): string {
  if (ms === null) return pendingWhenNull ? "…" : "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

export function StatusBar(p: StatusBarProps): JSX.Element {
  // Static config: labels and flags never change; only the value accessors
  // reactively pull from props. Defining once means <For> sees stable refs.
  const itemsConfig: Item[] = [
    { label: "デコード", value: () => p.decodeMs() },
    { label: "比較", value: () => p.diffMs() },
    { label: "行整形", value: () => p.buildRowsMs(), detailOnly: true },
    { label: "ハイライト", value: () => p.highlightMs(), pendingWhenNull: () => !p.highlightDisabled() },
    { label: "折り返し", value: () => p.wrapLayoutMs(), detailOnly: true, hideWhenNull: true },
    { label: "検索", value: () => p.searchMs(), detailOnly: true, hideWhenNull: true },
    { label: "言語定義読込", value: () => p.grammarLoadMs(), detailOnly: true, hideWhenNull: true },
    { label: "ミニマップ", value: () => p.minimapMs(), detailOnly: true, hideWhenNull: true },
  ];

  const visible = createMemo<Item[]>(() =>
    itemsConfig.filter((it) => {
      if (!p.detailed() && it.detailOnly) return false;
      if (it.hideWhenNull && it.value() === null) return false;
      return true;
    })
  );

  return (
    <footer
      class="fd-statusbar"
      role="status"
      aria-label="performance timings"
    >
      <For each={visible()}>
        {(it, idx) => (
          <>
            <Show when={idx() > 0}>
              <span class="fd-statusbar-sep">·</span>
            </Show>
            <span class="fd-statusbar-item">
              <span class="fd-statusbar-label">{it.label}:</span>
              <span class={`fd-statusbar-value${it.value() === null && (it.pendingWhenNull?.() ?? false) ? " fd-statusbar-pending" : ""}`}>
                {formatMs(it.value(), it.pendingWhenNull?.() ?? false)}
              </span>
            </span>
          </>
        )}
      </For>
    </footer>
  );
}
